/**
 * The voice-to-video consent protocol. Each message is a reliable LiveKit data
 * packet on VIDEO_TOPIC that carries UTF-8 JSON:
 * {"v":1,"kind":"request"|"accept"|"decline"|"cancel","rid":"<id>"}.
 * The wire contract is shared with the mobile app's call video switch. Change both together.
 */

import { createUuid } from '../../modules/create-uuid.js';

export const VIDEO_TOPIC = 'call.video';
export const VIDEO_PROTOCOL_VERSION = 1;
export const VIDEO_KINDS = ['request', 'accept', 'decline', 'cancel'];
// A request that gets no answer in this window is canceled.
export const VIDEO_REQUEST_TIMEOUT_MS = 30000;
// How long a declined / no-answer / camera-blocked notice stays on screen.
export const VIDEO_NOTICE_MS = 4000;

const MAX_RID_LENGTH = 64;
// Matches the mobile client: the cancels sent again after a room restart.
const RESENT_CANCEL_LIMIT = 8;
const CAMERA_DENIED_ERRORS = ['NotAllowedError', 'PermissionDeniedError', 'SecurityError'];

/** The notice for a camera that did not start: a denied permission, or any device failure. */
export function cameraFailureNotice(error) {
    return error && CAMERA_DENIED_ERRORS.includes(error.name) ? 'camera_blocked' : 'camera_unavailable';
}

// A rid needs uniqueness within one call only, so a browser without crypto gets a time-based id.
export function newVideoRid() {
    return createUuid() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** @returns {Uint8Array} */
export function encodeVideoSignal(kind, rid) {
    return new TextEncoder().encode(JSON.stringify({ v: VIDEO_PROTOCOL_VERSION, kind, rid }));
}

/**
 * @param {Uint8Array} payload
 * @returns {{kind: string, rid: string}|null} null for a packet this version must ignore
 */
export function decodeVideoSignal(payload) {
    let message;
    try {
        message = JSON.parse(new TextDecoder().decode(payload));
    } catch {
        return null;
    }
    if (!message || typeof message !== 'object' || message.v !== VIDEO_PROTOCOL_VERSION) {
        return null;
    }
    const { kind, rid } = message;
    if (!VIDEO_KINDS.includes(kind) || typeof rid !== 'string' || rid === '' || rid.length > MAX_RID_LENGTH) {
        return null;
    }
    return { kind, rid };
}

/**
 * Per-call consent state for the voice-to-video switch. call-state.js owns one
 * instance and supplies the media side through the ports:
 *   send(kind, rid)   publish one signal to the peer, resolve true once it is sent
 *   probeCamera()     open the camera without publishing it: resolve true, false with no camera API,
 *                     or reject with the getUserMedia error
 *   turnCameraOn()    publish the local camera (only called once allowed)
 *   turnCameraOff()   unpublish the local camera
 *   onChange()        the snapshot fields below changed
 */
export class VideoSwitch {
    constructor({ send, probeCamera, turnCameraOn, turnCameraOff, onChange, setTimer, clearTimer }) {
        this._send = send;
        this._probeCamera = probeCamera;
        this._turnCameraOn = turnCameraOn;
        this._turnCameraOff = turnCameraOff;
        this._onChange = onChange;
        this._setTimer = setTimer || ((fn, ms) => setTimeout(fn, ms));
        this._clearTimer = clearTimer || ((id) => clearTimeout(id));
        this.allowed = false;
        this.outgoingRid = null;
        this.incomingRid = null;
        this.notice = '';
        this._probing = false;
        this._accepting = false;
        this._acceptedRid = null;
        this._sentCancelRids = [];
        this._placedAsVideo = false;
        this._generation = 0;
        this._outgoingTimer = null;
        this._incomingTimer = null;
        this._noticeTimer = null;
    }

    /** A call answered or placed as video starts with consent. */
    reset(allowed) {
        this.dispose();
        this._generation += 1;
        this.allowed = !!allowed;
        this._placedAsVideo = !!allowed;
        this.outgoingRid = null;
        this.incomingRid = null;
        this.notice = '';
        this._probing = false;
        this._accepting = false;
        this._acceptedRid = null;
        this._sentCancelRids = [];
    }

    dispose() {
        this._stopTimer('_outgoingTimer');
        this._stopTimer('_incomingTimer');
        this._stopTimer('_noticeTimer');
    }

    /** 'outgoing' while our request waits, 'incoming' while the peer's prompt shows. */
    pending() {
        // A crossed request stays open after a glare grant, but it is not shown.
        if (this.allowed) {
            return '';
        }
        if (this.outgoingRid) {
            return 'outgoing';
        }
        return this.incomingRid ? 'incoming' : '';
    }

    async request() {
        if (this.allowed || this.outgoingRid || this._probing) {
            return;
        }
        if (this.incomingRid) {
            await this.accept();
            return;
        }
        const generation = this._generation;
        const failure = await this._probe();
        if (generation !== this._generation) {
            return;
        }
        if (failure) {
            this.showNotice(failure);
            return;
        }
        // A request can arrive while the permission prompt is open.
        if (this.incomingRid) {
            await this._sendAccept(this.incomingRid);
            return;
        }
        const rid = newVideoRid();
        this._clearNotice();
        this.outgoingRid = rid;
        this._send('request', rid).then((sent) => {
            if (!sent && this.outgoingRid === rid && !this.allowed) {
                this._clearOutgoing();
                this.showNotice('request_failed');
            }
        });
        this._armOutgoingTimer(rid);
        this._onChange();
    }

    cancel() {
        const rid = this.outgoingRid;
        if (!rid || this.allowed) {
            return;
        }
        this._sendCancel(rid);
        this._clearOutgoing();
        this._onChange();
    }

    // The camera must open before the accept goes out, so a blocked camera never grants consent.
    async accept() {
        const rid = this.incomingRid;
        if (!rid || this._accepting || this._probing) {
            return;
        }
        const generation = this._generation;
        const failure = await this._probe();
        if (generation !== this._generation || this.incomingRid !== rid) {
            return;
        }
        if (failure) {
            this.showNotice(failure);
            return;
        }
        await this._sendAccept(rid);
    }

    // Consent starts only once the accept is sent. A failed send keeps the prompt for a retry.
    async _sendAccept(rid) {
        if (this._accepting) {
            return;
        }
        const generation = this._generation;
        this._accepting = true;
        const sent = await this._send('accept', rid);
        if (generation !== this._generation) {
            return;
        }
        this._accepting = false;
        if (!sent && this.incomingRid === rid && !this._incomingTimer) {
            // The prompt expired while the send was in flight.
            this._clearIncoming();
            this._onChange();
        }
        if (!sent || this.incomingRid !== rid) {
            return;
        }
        this._clearIncoming();
        this._allow();
        this._acceptedRid = rid;
        this._onChange();
        await this._turnCameraOn();
    }

    decline() {
        const rid = this.incomingRid;
        // An accept already on the wire cannot be taken back by a decline.
        if (!rid || this._accepting) {
            return;
        }
        this._send('decline', rid);
        this._clearIncoming();
        this._onChange();
    }

    /** @param {{kind: string, rid: string}} signal a decoded packet from the peer */
    receive({ kind, rid }) {
        switch (kind) {
            case 'request':
                this._receiveRequest(rid);
                break;
            case 'cancel':
                if (rid === this.incomingRid) {
                    this._clearIncoming();
                    this._onChange();
                } else if (rid === this._acceptedRid && !this._placedAsVideo) {
                    // The requester gave up before our accept arrived, so it never took the consent.
                    this._acceptedRid = null;
                    this.allowed = false;
                    if (this.outgoingRid && !this._outgoingTimer) {
                        this._armOutgoingTimer(this.outgoingRid);
                    }
                    this._onChange();
                    this._turnCameraOff();
                }
                break;
            case 'accept':
                if (rid === this.outgoingRid && this.allowed) {
                    // The peer granted our own request, so a lost cancel of its crossed request must not revoke.
                    this._acceptedRid = null;
                    this._clearOutgoing();
                } else if (rid === this.outgoingRid) {
                    this._allow();
                    this._onChange();
                    this._turnCameraOn();
                }
                break;
            case 'decline':
                if (rid === this.outgoingRid && this.allowed) {
                    this._clearOutgoing();
                } else if (rid === this.outgoingRid) {
                    this._clearOutgoing();
                    this.showNotice('declined');
                }
                break;
            default:
                break;
        }
    }

    showNotice(kind) {
        this._stopTimer('_noticeTimer');
        this.notice = kind;
        this._noticeTimer = this._setTimer(() => {
            this._noticeTimer = null;
            this.notice = '';
            this._onChange();
        }, VIDEO_NOTICE_MS);
        this._onChange();
    }

    _receiveRequest(rid) {
        if (rid === this.incomingRid) {
            return;
        }
        if (this.allowed) {
            this._acceptedRid = rid;
            this._send('accept', rid);
            return;
        }
        if (this.outgoingRid) {
            // Both sides asked at once: each request answers the other.
            // Our own request stays open, so the peer's answer to it still counts after a revoke.
            this._clearIncoming();
            this.allowed = true;
            this._clearNotice();
            this._acceptedRid = rid;
            this._send('accept', rid);
            this._onChange();
            this._turnCameraOn();
            return;
        }
        this._clearIncoming();
        this._clearNotice();
        this.incomingRid = rid;
        // The sender cancels after the same window. This covers a cancel lost in a reconnect.
        this._incomingTimer = this._setTimer(() => {
            this._incomingTimer = null;
            // An accept on the wire settles the prompt. A failed send clears it in _sendAccept.
            if (this.incomingRid === rid && !this._accepting) {
                this.incomingRid = null;
                this._onChange();
            }
        }, VIDEO_REQUEST_TIMEOUT_MS);
        this._onChange();
    }

    _onRequestTimeout(rid) {
        this._outgoingTimer = null;
        if (this.outgoingRid !== rid) {
            return;
        }
        // A cancel here would revoke the consent the peer granted to our crossed request.
        // The rid stays open, so a late answer still counts, and a revoke restarts the timer.
        if (this.allowed) {
            return;
        }
        this._sendCancel(rid);
        this.outgoingRid = null;
        this.showNotice('no_answer');
    }

    /** Send the recent cancels again. A cancel lost in a room restart otherwise leaves the peer's grant in place. */
    resendCancel() {
        this._sentCancelRids.forEach((rid) => this._send('cancel', rid));
    }

    _armOutgoingTimer(rid) {
        this._outgoingTimer = this._setTimer(() => this._onRequestTimeout(rid), VIDEO_REQUEST_TIMEOUT_MS);
    }

    _sendCancel(rid) {
        this._sentCancelRids = [...this._sentCancelRids.filter((sent) => sent !== rid), rid].slice(-RESENT_CANCEL_LIMIT);
        this._send('cancel', rid);
    }

    _allow() {
        this.allowed = true;
        this._clearOutgoing();
        this._clearNotice();
    }

    /** @returns {Promise<string>} '' when the camera opened, else the failure notice */
    async _probe() {
        const generation = this._generation;
        this._probing = true;
        let failure = '';
        try {
            failure = (await this._probeCamera()) ? '' : 'camera_unavailable';
        } catch (error) {
            failure = cameraFailureNotice(error);
        }
        if (generation === this._generation) {
            this._probing = false;
        }
        return failure;
    }

    _clearNotice() {
        this._stopTimer('_noticeTimer');
        this.notice = '';
    }

    _clearOutgoing() {
        this.outgoingRid = null;
        this._stopTimer('_outgoingTimer');
    }

    _clearIncoming() {
        this.incomingRid = null;
        this._stopTimer('_incomingTimer');
    }

    _stopTimer(name) {
        if (this[name]) {
            this._clearTimer(this[name]);
            this[name] = null;
        }
    }
}
